import { useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AxiosInstanceFactory from '@src/utils/axiosInstance';
import { handleError } from '../../utils/errors';

import * as routes from '@src/app_routes/ringBuilder';

type TProps = {
  setSynchronized: (S: boolean) => void
};

const axiosInstance = AxiosInstanceFactory();

export default function DyorServerDataSynchronizer (props: TProps) {
  const dispatch = useDispatch();
  const {
    currentDiamondId,
    currentSettingSku,
    skipSync
  } = useSelector((state) => ({
    skipSync: state.settingsFromServer.skipDyorDataRefresh,
    currentSettingSku: state.ringBuilder.setting.sku,
    currentDiamondId: state.ringBuilder.diamond.id
  }));
  const { setSynchronized } = props;

  const currentSettingSkuRef = useRef(currentSettingSku);
  const currentDiamondIdRef = useRef(currentDiamondId);

  // We have to keep refs up to date so that the onfocus callback function
  // always has access to the latest values.
  useEffect(
    () => {
      currentSettingSkuRef.current = currentSettingSku;
      currentDiamondIdRef.current = currentDiamondId;
    },
    [currentSettingSku, currentDiamondId]
  );

  const synchronize = async () => {
    try {
      // These intentionally shadow outer variables so that we can't
      // accidentally access the outer variables and get stale values.
      const currentSettingSku = currentSettingSkuRef.current;
      const currentDiamondId = currentDiamondIdRef.current;
      const settingRes = await axiosInstance.get(routes.apiSettingPath);
      const diamondRes = await axiosInstance.get(routes.apiDiamondPath);
      const settingSku = settingRes.data;
      const diamondId = diamondRes.data;

      if (settingSku) {
        if (currentSettingSku !== settingSku) {
          dispatch({ type: 'UPDATE_DYOR_SETTING_SKU', payload: settingSku });
        }
      } else if (currentSettingSku || currentSettingSku === null) {
        // It is possible for the sku to be cached as null, which causes 0
        // results to be displayed, with no way to fix it. We need to explicit
        // null check otherwise this clause won't run.
        dispatch({ type: 'PROCESS_CLEARED_DYOR_SETTING' });
      }

      if (diamondId) {
        if (currentDiamondId !== diamondId) {
          dispatch({ type: 'UPDATE_DYOR_DIAMOND_ID', payload: diamondId });
        }
      } else if (currentDiamondId || diamondId === null) {
        // It is possible for the id to be cached as null, which causes 0
        // results to be displayed, with no way to fix it. We need to explicit
        // null check otherwise this clause won't run.
        dispatch({ type: 'PROCESS_CLEARED_DYOR_DIAMOND' });
      }

      setSynchronized(true);
      dispatch({ type: 'DYOR_SERVER_PULL_COMPLETE' });
    } catch (e) {
      handleError(e);
    }
  };

  useEffect(() => {
    // In most cases, we pre-load dyor data on the server side. Sometimes we need
    // to render first. If we pre-load, we set a key skipDyorDataRefresh to let
    // our client side know that.
    if (skipSync) {
      dispatch({ type: 'DYOR_SERVER_PULL_COMPLETE' });
      setSynchronized(true);
    } else {
      synchronize();
    }

    window && window.addEventListener('focus', synchronize);

    return () => window && window.removeEventListener('focus', synchronize);
  }, []);

  return null;
}
